import React from 'react'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { FormattedRelativeTime, FormattedMessage } from 'react-intl'
import { selectRelativeUnits } from '../../react-intl-helpers'
import { VulnerabilityTitle } from '../../vulnerability-title'
import {
  readOnlyGridCss,
  metadataCss
} from '../../../legacy-design-system/text'
import { CellRenderer, CellProps } from '../../../legacy-design-system/grid'
import { LoadingTextMask } from '../../../legacy-design-system/loading-masks'
import { AppUnlicensedLink } from '../../link'
import { TagList } from '../../../legacy-design-system/tags'
import { PolicyViolation } from '../../policy-violation'
import { WithTooltip, TooltipLabel, TooltipList } from '../../../design-system'
import { DateTime } from '../shared'
import { AgentLanguage } from '../../agent-language'
import { VulnerabilityColumnArgs } from '../grid-columns'
import { RouteContext } from '../../route-provider'
import { ModifyVulnerabilityTagsDialog } from '../actions/tag-dialog'
import { Role } from '@contrast-security-inc/base-types'
import { Link } from '@contrast-security-inc/design-system-typography'

export const detectedStatusTestID = 'vuln-detected-status-test-id'

const cellCss = css`
  padding-left: 8px;
  padding-right: 16px;
  width: 100%;
  overflow: hidden;
`

const topRowCss = css`
  display: flex;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
`

const nameContainerCss = (hasTags) => css`
  max-width: ${!hasTags ? '100%' : 'calc(100% - 46px)'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const nameCss = css`
  flex-shrink: 0;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  line-height: 1.2em;
  white-space: nowrap;
`

const visibleNameCss = nameCss
const violationNameCss = nameCss
const notVisibleNameCss = cx(nameCss, readOnlyGridCss)

const tagsContainerCss = css`
  display: flex;
  margin-left: 4px;
  overflow: hidden;
`

const metadataRowCss = css`
  flex-grow: 1;
  margin-top: 1px;
  display: inline-block;
`

const violationIndicatorCss = css`
  flex-grow: 0;
  margin-left: 5px;
`
interface Violation {
  name: string
  id: string
}

export interface Vulnerability {
  id: string
  title: string
  ruleName: string
  visible: boolean
  tags: string[]
  firstDetected: DateTime
  lastDetected: DateTime
  violations: Violation[]
  application: {
    id: string
    name: string
    language: AgentLanguage
    role: Role
  }
}

export interface SinkGroup {
  sink: string
  sinkHash: string
  ruleName: string
  title: string
  count: number
}

interface RowData {
  sinkGroup?: SinkGroup
  vulnerability?: Vulnerability
}

export function getVulnerabilityCellRenderer({
  organizationID,
  organizationRole,
  hasLicenses,
  features,
  getDrilldownPath,
  dateField,
  refresh,
  refetchQueries
}: VulnerabilityColumnArgs): CellRenderer<RowData> {
  return ({ rowData, getCellContentProps }) => {
    if (rowData.vulnerability) {
      return renderVulnerabilityCell({
        organizationID,
        organizationRole,
        hasLicenses,
        features,
        getDrilldownPath,
        dateField,
        refresh,
        refetchQueries,
        rowData: rowData.vulnerability,
        getCellContentProps
      })
    } else if (rowData.sinkGroup) {
      return renderSinkGroupVulnerabilityCell({
        rowData: rowData.sinkGroup,
        getCellContentProps
      })
    }
    throw new Error(
      `Invalid vulnerability row. Row data should contain a vulnerability or sink group: ${rowData}`
    )
  }
}

function renderVulnerabilityCell(
  props: VulnerabilityColumnArgs & CellProps<Vulnerability>
) {
  return <VulnerabilityCell {...props} />
}

function VulnerabilityCell({
  organizationID,
  organizationRole,
  hasLicenses,
  features,
  getDrilldownPath,
  dateField,
  refresh,
  refetchQueries,
  rowData: {
    id,
    title,
    ruleName,
    visible,
    tags,
    firstDetected,
    lastDetected,
    violations,
    application: {
      id: applicationID,
      name: applicationName,
      language: applicationLanguage,
      role
    }
  },
  getCellContentProps
}: VulnerabilityColumnArgs & CellProps<Vulnerability>) {
  const { getQueryParamsAsString } = React.useContext(RouteContext)
  const [tagsWidth, setTagsWidth] = React.useState(0)
  const [showTagDialog, setShowTagDialog] = React.useState(false)
  const qs = getQueryParamsAsString()
  const queryString = qs ? `?${qs}` : ''

  const renderedTitle = (
    <VulnerabilityTitle title={title} ruleName={ruleName} visible={visible} />
  )

  const nameProps = getCellContentProps({
    className: nameContainerCss(tags.length)
  })

  const violationindicatorProps = getCellContentProps({
    className: violationIndicatorCss
  })

  const tagsContainerProps = getCellContentProps({
    className: tagsContainerCss
  })

  const metadataRowProps = getCellContentProps({
    className: metadataRowCss
  })

  const cellRef = React.useRef<HTMLDivElement>(null)
  const titleRef = React.useRef<HTMLDivElement>(null)
  const policiesRef = React.useRef<HTMLElement>(null)
  React.useLayoutEffect(() => {
    const width =
      (cellRef?.current?.clientWidth || 0) -
      (titleRef?.current?.offsetWidth || 0) -
      (policiesRef?.current?.offsetWidth || 0)
    setTagsWidth(width)
  }, [tags, cellRef, titleRef, policiesRef])

  return (
    <div className={cellCss}>
      <div className={topRowCss} ref={cellRef}>
        <div {...nameProps} ref={titleRef}>
          <WithTooltip
            renderTooltipContent={() => {
              return visible ? (
                <TooltipContent id={id} />
              ) : (
                <FormattedMessage id="detail_vulnerabilityTrace_hiddenVulnDetails">
                  {(message) => {
                    return (
                      <React.Fragment>
                        <AppUnlicensedLink
                          organizationID={organizationID}
                          applicationID={applicationID}
                          applicationLanguage={applicationLanguage}
                          applicationName={applicationName}
                          hasLicenses={hasLicenses}
                          archived={false}
                          role={role}
                          features={features}
                          refresh={refresh}
                          refetchQueries={refetchQueries}
                          className={css`
                            color: ${theme.color.turquoise50} !important;
                          `}
                        />
                        <div
                          className={css`
                            max-width: 250px;
                            white-space: normal;
                          `}
                        >
                          {message}
                        </div>
                      </React.Fragment>
                    )
                  }}
                </FormattedMessage>
              )
            }}
          >
            {() => {
              return visible ? (
                <Link
                  color={violations.length > 0 ? 'red50' : 'neutral100'}
                  className={
                    violations.length > 0 ? violationNameCss : visibleNameCss
                  }
                  href={`${getDrilldownPath(id)}${queryString}`}
                  data-e2e="vuln-name"
                >
                  {renderedTitle}
                </Link>
              ) : (
                <span className={notVisibleNameCss}>{renderedTitle}</span>
              )
            }}
          </WithTooltip>
        </div>
        {visible && violations.length > 0 && (
          <span {...violationindicatorProps} ref={policiesRef}>
            <PolicyViolation
              organizationID={organizationID}
              organizationRole={organizationRole}
              violations={violations}
            />
          </span>
        )}
        <TagList
          onClickOverflow={() => setShowTagDialog(true)}
          width={tagsWidth}
          tags={tags}
          {...tagsContainerProps}
        />
        {showTagDialog &&
          refetchQueries && //  we only check these
          refresh && ( // other vars because typescript
            <ModifyVulnerabilityTagsDialog
              organizationID={organizationID}
              vulnerabilityIDs={[id]}
              refetchQueries={refetchQueries}
              refresh={refresh}
              closeDialog={() => setShowTagDialog(false)}
              initialTags={tags}
              isOpen={true}
            />
          )}
      </div>
      <div {...metadataRowProps} data-testid={detectedStatusTestID}>
        <FormattedMessage
          id={
            dateField === 'FIRST'
              ? 'detail_lastDetected_vulnerability'
              : 'detail_firstDetected_vulnerability'
          }
        >
          {(text) => {
            const date = dateField === 'FIRST' ? lastDetected : firstDetected
            const { value, unit } = selectRelativeUnits(date)
            return (
              <FormattedRelativeTime value={value} unit={unit} numeric="auto">
                {(relativeText) => (
                  <span className={metadataCss}>
                    {text} {relativeText}
                  </span>
                )}
              </FormattedRelativeTime>
            )
          }}
        </FormattedMessage>
      </div>
    </div>
  )
}

interface TooltipProps {
  id: string
}

function TooltipContent({ id }: TooltipProps) {
  return (
    <TooltipList>
      <li>
        <FormattedMessage id="title_identifier">
          {(label) => {
            return <TooltipLabel label={label as any} renderValue={() => id} />
          }}
        </FormattedMessage>
      </li>
    </TooltipList>
  )
}

const containerLoadingCss = css`
  flex-grow: 1;
  width: 100%;
`

const topRowLoadingCss = cx(
  topRowCss,
  css`
    display: flex;
    flex-direction: column;
    width: 60%;
  `
)

const maskedNameCss = css`
  width: 100%;
  padding: 1px 0px;
`

const maskedMetadataCss = cx(
  maskedNameCss,
  css`
    font-size: 0.6em;
  `
)

export function loadingVulnCellRenderer() {
  return (
    <div className={cellCss}>
      <div className={containerLoadingCss}>
        <div className={topRowLoadingCss}>
          <div className={maskedNameCss}>
            <LoadingTextMask min={7} />
          </div>
          <div className={maskedMetadataCss}>
            <LoadingTextMask min={3} max={4} />
          </div>
        </div>
      </div>
    </div>
  )
}

const bubbleCss = css`
  padding: 11px 14px;
  display: block;
  border-radius: 30px;
  text-align: center;
  background: ${theme.color.neutral80};
  color: ${theme.color.white};
`

export const sinkGroupLinkTestId = 'sink-group-drilldown-link'

function renderSinkGroupVulnerabilityCell({
  rowData: { sink, sinkHash, ruleName, title, count }
}: CellProps<SinkGroup>) {
  return (
    <RouteContext.Consumer>
      {({ setQueryParameters }) => {
        return (
          <div className={topRowCss}>
            <div className={bubbleCss}>{count}</div>
            <div
              className={cx(
                nameContainerCss(false),
                css`
                  margin-left: 15px;
                `
              )}
            >
              <div
                className={css`
                  display: flex;
                `}
              >
                <WithTooltip renderTooltipContent={() => <span>{sink}</span>}>
                  {() => (
                    <Link
                      color="neutral100"
                      className={visibleNameCss}
                      data-testid={sinkGroupLinkTestId}
                      onClick={() => {
                        const params = new Map()
                        params.set('sinks', sinkHash)
                        params.set('types', ruleName)
                        params.set('drilldown', 'true')
                        params.set('groupedBy', null)
                        setQueryParameters('vulns', params)
                      }}
                    >
                      {title}
                    </Link>
                  )}
                </WithTooltip>
              </div>

              <span className={metadataCss}>{sink}</span>
            </div>
          </div>
        )
      }}
    </RouteContext.Consumer>
  )
}
